import { create } from 'zustand'
import {
  DEFAULT_RESPONSE_TARGET,
  EMPTY_TAB_CONFIRMATIONS,
  type BudgetTabConfirmations,
  type BudgetWizardTab,
} from '@/features/instruments/publish/campaign-budget/lib/campaignBudgetConstants'
import { DEFAULT_SELECTED_CHANNELS } from '@/features/instruments/publish/distribution-channels/lib/distributionChannelCatalog'
import type { DistributionChannelId } from '@/features/instruments/publish/distribution-channels/lib/distributionChannelTypes'
import {
  CHANNEL_CONTENT_KEYS,
  hasStoredChannelContent,
  type ChannelContentByKey,
  type ChannelContentDraft,
  type ChannelContentKey,
  type ChannelContentPatch,
} from '@/features/instruments/publish/distribution-channels/lib/channelContentTypes'
import { DEFAULT_REWARD_ENABLED } from '@/features/instruments/publish/shared/lib/rewardEnabled'
import type { PublishWizardStepNumber } from '@/features/instruments/publish/shared/lib/publishWizardSteps'
import type { AudienceFilters, DemographicPanelId } from '@/features/instruments/publish/target-audience/lib/audienceTypes'
import { coerceAudienceFilters } from '@/features/instruments/publish/target-audience/lib/deviceTypeFilter'
import { computeUserModifiedPanelIds } from '@/features/instruments/publish/target-audience/lib/audienceFilterLogic'
import { buildPublishTargetAudienceSnapshot } from '@/features/instruments/publish/shared/lib/publishTargetAudience'
import type { HydrateFromDraftParams } from './publishStoreHydration'
import type {
  AudienceAvailability,
  ProfileAttributeFilter,
  ResponseTargetingMode,
  ScientificConfidenceLevel,
} from '@brushfire/types'
import { AUDIENCE_AVAILABILITY_EXISTING_ONLY, readAudienceAvailability } from '@brushfire/types'
import {
  DEFAULT_CONFIDENCE_LEVEL,
  DEFAULT_MARGIN_OF_ERROR_PERCENT,
  reconcileStep4PhaseForRewardEnabled,
  resolveStep4Phase,
  type Step4Phase,
} from '@/features/instruments/publish/campaign-budget/lib/targetingModeConstants'

export type { HydrateFromDraftParams } from './publishStoreHydration'

/** Full replacement PATCH: absent channels are sent as `null` so a restored default is persisted. */
function channelContentPatchFromDraft(content: ChannelContentDraft): ChannelContentPatch {
  const patch: ChannelContentPatch = {}
  for (const key of CHANNEL_CONTENT_KEYS) {
    Object.assign(patch, { [key]: content[key] ?? null })
  }
  return patch
}

interface PublishStore {
  aiSuggestedFilters: AudienceFilters
  suggestedPanelIds: DemographicPanelId[]
  userModifiedPanelIds: DemographicPanelId[]
  filtersModified: boolean
  aiSuggestionsApplied: boolean
  filters: AudienceFilters
  profileAttributeFilters: ProfileAttributeFilter[]
  selectedChannels: DistributionChannelId[]
  /** BFP-1144 — per-channel customizations; a missing channel uses the system default. */
  channelContent: ChannelContentDraft
  /**
   * False until this session has loaded or edited channel copy.
   * Save Draft omits `channelContent` until then so a store reset cannot
   * PATCH every channel to null and wipe the saved message.
   */
  channelContentSynced: boolean
  rewardEnabled: boolean
  budgetConfigured: boolean
  responseTarget: number | null
  rewardPerResponse: number | null
  activeBudgetTab: BudgetWizardTab
  budgetTabConfirmations: BudgetTabConfirmations
  completedSteps: Set<PublishWizardStepNumber>
  maxReachedStep: PublishWizardStepNumber
  audienceMatchCount: number | undefined
  targetingMode: ResponseTargetingMode | null
  confidenceLevel: ScientificConfidenceLevel
  marginOfErrorPercent: number
  scientificTargetLocked: boolean
  step4Phase: Step4Phase
  audienceAvailability: AudienceAvailability
  initialized: boolean
  initializeTargetAudience: (params: {
    filters: AudienceFilters
    aiSuggestedFilters: AudienceFilters
    suggestedPanelIds: DemographicPanelId[]
    userModifiedPanelIds: DemographicPanelId[]
    filtersModified?: boolean
    aiSuggestionsApplied?: boolean
  }) => void
  hydrateFromDraft: (params: HydrateFromDraftParams) => void
  hydrateAiSuggestionsOnly: (params: {
    aiSuggestedFilters: AudienceFilters
    suggestedPanelIds: DemographicPanelId[]
  }) => void
  setFilters: (filters: AudienceFilters) => void
  setProfileAttributeFilters: (filters: ProfileAttributeFilter[]) => void
  patchFilters: (patch: Partial<AudienceFilters>) => void
  setSelectedChannels: (channels: DistributionChannelId[]) => void
  setChannelContent: <K extends ChannelContentKey>(
    channel: K,
    content: ChannelContentByKey[K] | null,
  ) => void
  setRewardEnabled: (enabled: boolean) => void
  setBudgetConfigured: (configured: boolean) => void
  setResponseTarget: (target: number | null) => void
  setRewardPerResponse: (reward: number | null) => void
  setActiveBudgetTab: (tab: BudgetWizardTab) => void
  setBudgetTabConfirmations: (confirmations: BudgetTabConfirmations) => void
  patchBudgetTabConfirmations: (patch: Partial<BudgetTabConfirmations>) => void
  clearBudgetConfiguration: () => void
  clearAllFilters: () => void
  restoreAiSuggestedFilters: () => void
  markStepComplete: (step: PublishWizardStepNumber) => void
  setMaxReachedStep: (step: PublishWizardStepNumber) => void
  setAudienceMatchCount: (count: number | undefined) => void
  setTargetingMode: (mode: ResponseTargetingMode | null) => void
  setConfidenceLevel: (level: ScientificConfidenceLevel) => void
  setMarginOfErrorPercent: (percent: number) => void
  setScientificTargetLocked: (locked: boolean) => void
  setStep4Phase: (phase: Step4Phase) => void
  setAudienceAvailability: (value: AudienceAvailability) => void
  clearScientificTargeting: () => void
  reset: () => void
}

const emptyFilters: AudienceFilters = {}

function withRecomputedModifiedPanels(state: {
  filters: AudienceFilters
  aiSuggestedFilters: AudienceFilters
  suggestedPanelIds: DemographicPanelId[]
}): Pick<PublishStore, 'filters' | 'userModifiedPanelIds' | 'filtersModified'> {
  const filters = coerceAudienceFilters(state.filters)
  return {
    filters,
    userModifiedPanelIds: computeUserModifiedPanelIds(
      filters,
      state.aiSuggestedFilters,
      state.suggestedPanelIds,
    ),
    filtersModified: true,
  }
}

function audienceAvailabilitySwitchPatch(
  value: AudienceAvailability,
  state: Pick<PublishStore, 'budgetTabConfirmations' | 'responseTarget'>,
): Partial<PublishStore> {
  if (value === 'existing_and_future') {
    const unsetTarget = state.budgetTabConfirmations.responseTarget
      ? {}
      : {
          responseTarget: null as number | null,
          activeBudgetTab: 'response_target' as BudgetWizardTab,
        }
    return {
      targetingMode: 'standard',
      scientificTargetLocked: false,
      step4Phase: 'standard_tabs',
      audienceMatchCount: undefined,
      ...unsetTarget,
    }
  }
  // `existing_only` and BFP-1141 `all_registered` share budget semantics
  // (response target required; standard / scientific allowed). Any mode switch
  // invalidates the cached match count because the audience pool changed.
  return {
    audienceMatchCount: undefined,
    ...(state.responseTarget == null ? { responseTarget: DEFAULT_RESPONSE_TARGET } : {}),
  }
}

/** BFP-1141 — All Registered Users ignores every filter; drop them so the draft persists `{}`. */
function filtersForAudienceAvailability(
  value: AudienceAvailability,
  filters: AudienceFilters,
): AudienceFilters {
  if (value === 'all_registered') return emptyFilters
  if (value === 'existing_and_future') {
    const next = { ...filters }
    delete next.ekycStatus
    return next
  }
  return filters
}

const initialBudgetState = {
  responseTarget: DEFAULT_RESPONSE_TARGET,
  rewardPerResponse: null as number | null,
  activeBudgetTab: 'response_target' as BudgetWizardTab,
  budgetTabConfirmations: { ...EMPTY_TAB_CONFIRMATIONS },
}

const initialState = {
  aiSuggestedFilters: emptyFilters,
  suggestedPanelIds: [] as DemographicPanelId[],
  userModifiedPanelIds: [] as DemographicPanelId[],
  filtersModified: false,
  aiSuggestionsApplied: false,
  filters: emptyFilters,
  profileAttributeFilters: [] as ProfileAttributeFilter[],
  selectedChannels: [...DEFAULT_SELECTED_CHANNELS] as DistributionChannelId[],
  channelContent: {} as ChannelContentDraft,
  channelContentSynced: false,
  rewardEnabled: DEFAULT_REWARD_ENABLED,
  budgetConfigured: false,
  ...initialBudgetState,
  completedSteps: new Set<PublishWizardStepNumber>(),
  maxReachedStep: 1 as PublishWizardStepNumber,
  audienceMatchCount: undefined as number | undefined,
  targetingMode: null as ResponseTargetingMode | null,
  confidenceLevel: DEFAULT_CONFIDENCE_LEVEL,
  marginOfErrorPercent: DEFAULT_MARGIN_OF_ERROR_PERCENT,
  scientificTargetLocked: false,
  step4Phase: 'mode_selection' as Step4Phase,
  audienceAvailability: AUDIENCE_AVAILABILITY_EXISTING_ONLY,
  initialized: false,
}

export const usePublishStore = create<PublishStore>((set) => ({
  ...initialState,
  initializeTargetAudience: ({
    filters,
    aiSuggestedFilters,
    suggestedPanelIds,
    userModifiedPanelIds,
    filtersModified,
    aiSuggestionsApplied,
  }) =>
    set({
      aiSuggestedFilters: structuredClone(coerceAudienceFilters(aiSuggestedFilters)),
      filters: structuredClone(coerceAudienceFilters(filters)),
      suggestedPanelIds: [...suggestedPanelIds],
      userModifiedPanelIds: [...userModifiedPanelIds],
      filtersModified: filtersModified === true,
      aiSuggestionsApplied: aiSuggestionsApplied === true,
      initialized: true,
    }),
  hydrateAiSuggestionsOnly: ({ aiSuggestedFilters, suggestedPanelIds }) =>
    set((state) => ({
      aiSuggestedFilters: structuredClone(coerceAudienceFilters(aiSuggestedFilters)),
      suggestedPanelIds: [...suggestedPanelIds],
      userModifiedPanelIds: computeUserModifiedPanelIds(
        state.filters,
        aiSuggestedFilters,
        suggestedPanelIds,
      ),
    })),
  hydrateFromDraft: ({
    filters,
    selectedChannels,
    maxReachedStep,
    completedSteps,
    rewardEnabled,
    budgetConfigured,
    channelContent,
    campaignBudget,
    aiSuggestedFilters,
    suggestedPanelIds,
    userModifiedPanelIds,
    filtersModified,
    aiSuggestionsApplied,
    audienceMatchCount,
    targetingMode,
    confidenceLevel,
    marginOfErrorPercent,
    scientificTargetLocked,
    step4Phase,
    audienceAvailability,
    profileAttributeFilters,
  }) =>
    set((state) => ({
      aiSuggestedFilters: aiSuggestedFilters
        ? structuredClone(coerceAudienceFilters(aiSuggestedFilters))
        : state.aiSuggestedFilters,
      suggestedPanelIds: suggestedPanelIds
        ? [...suggestedPanelIds]
        : state.suggestedPanelIds,
      userModifiedPanelIds: userModifiedPanelIds
        ? [...userModifiedPanelIds]
        : state.userModifiedPanelIds,
      filtersModified: filtersModified ?? state.filtersModified,
      aiSuggestionsApplied: aiSuggestionsApplied ?? state.aiSuggestionsApplied,
      filters: structuredClone(coerceAudienceFilters(filters)),
      profileAttributeFilters: profileAttributeFilters
        ? profileAttributeFilters.map((filter) => ({
            ...filter,
            values: [...filter.values],
          }))
        : state.profileAttributeFilters,
      selectedChannels: [...selectedChannels],
      channelContent: hasStoredChannelContent(channelContent)
        ? { ...channelContent }
        : state.channelContent,
      channelContentSynced: hasStoredChannelContent(channelContent)
        ? true
        : state.channelContentSynced,
      rewardEnabled: rewardEnabled ?? state.rewardEnabled,
      budgetConfigured: budgetConfigured ?? state.budgetConfigured,
      responseTarget:
        campaignBudget && 'responseTarget' in campaignBudget
          ? (campaignBudget.responseTarget ?? null)
          : state.responseTarget,
      rewardPerResponse:
        campaignBudget?.rewardPerResponse === undefined
          ? state.rewardPerResponse
          : campaignBudget.rewardPerResponse,
      activeBudgetTab: campaignBudget?.activeBudgetTab ?? state.activeBudgetTab,
      budgetTabConfirmations: campaignBudget?.budgetTabConfirmations
        ? { ...campaignBudget.budgetTabConfirmations }
        : state.budgetTabConfirmations,
      maxReachedStep: Math.max(state.maxReachedStep, maxReachedStep) as PublishWizardStepNumber,
      completedSteps: new Set([...state.completedSteps, ...completedSteps]),
      audienceMatchCount: audienceMatchCount ?? state.audienceMatchCount,
      targetingMode: targetingMode ?? state.targetingMode,
      confidenceLevel: confidenceLevel ?? state.confidenceLevel,
      marginOfErrorPercent: marginOfErrorPercent ?? state.marginOfErrorPercent,
      scientificTargetLocked: scientificTargetLocked ?? state.scientificTargetLocked,
      step4Phase: step4Phase ?? resolveStep4Phase(targetingMode ?? state.targetingMode),
      audienceAvailability: audienceAvailability
        ? readAudienceAvailability({ audienceAvailability })
        : state.audienceAvailability,
      initialized: true,
    })),
  setAudienceMatchCount: (count) =>
    set({
      audienceMatchCount:
        typeof count === 'number' && Number.isFinite(count) && count >= 0
          ? Math.floor(count)
          : undefined,
    }),
  setTargetingMode: (mode) => set({ targetingMode: mode }),
  setConfidenceLevel: (level) => set({ confidenceLevel: level }),
  setMarginOfErrorPercent: (percent) => set({ marginOfErrorPercent: percent }),
  setScientificTargetLocked: (locked) => set({ scientificTargetLocked: locked }),
  setStep4Phase: (phase) => set({ step4Phase: phase }),
  setAudienceAvailability: (value) =>
    set((state) => {
      if (state.audienceAvailability === value) return state
      const nextFilters = filtersForAudienceAvailability(value, state.filters)
      return {
        audienceAvailability: value,
        ...withRecomputedModifiedPanels({
          ...state,
          filters: nextFilters,
        }),
        ...(value === 'all_registered' ? { aiSuggestionsApplied: false } : {}),
        ...audienceAvailabilitySwitchPatch(value, state),
      }
    }),
  clearScientificTargeting: () =>
    set({
      targetingMode: null,
      confidenceLevel: DEFAULT_CONFIDENCE_LEVEL,
      marginOfErrorPercent: DEFAULT_MARGIN_OF_ERROR_PERCENT,
      scientificTargetLocked: false,
      step4Phase: 'mode_selection',
    }),
  setProfileAttributeFilters: (profileAttributeFilters) =>
    set({
      profileAttributeFilters: profileAttributeFilters.map((filter) => ({
        ...filter,
        values: [...filter.values],
      })),
      audienceMatchCount: undefined,
    }),
  setFilters: (filters) =>
    set((state) =>
      withRecomputedModifiedPanels({
        ...state,
        filters: structuredClone(coerceAudienceFilters(filters)),
      }),
    ),
  patchFilters: (patch) =>
    set((state) =>
      withRecomputedModifiedPanels({
        ...state,
        filters: coerceAudienceFilters({ ...state.filters, ...patch }),
      }),
    ),
  setSelectedChannels: (channels) => set({ selectedChannels: [...channels] }),
  setChannelContent: (channel, content) =>
    set((state) => {
      const next = { ...state.channelContent }
      if (content == null) delete next[channel]
      else Object.assign(next, { [channel]: { ...content } })
      return { channelContent: next, channelContentSynced: true }
    }),
  setRewardEnabled: (enabled) =>
    set((state) => ({
      rewardEnabled: enabled,
      // No Rewards scientific uses standalone phase; Rewards must use tab layout.
      step4Phase: reconcileStep4PhaseForRewardEnabled(state.step4Phase, enabled),
      ...(enabled
        ? {}
        : {
            activeBudgetTab: 'response_target' as BudgetWizardTab,
          }),
    })),
  setBudgetConfigured: (configured) => set({ budgetConfigured: configured }),
  setResponseTarget: (target) => set({ responseTarget: target }),
  setRewardPerResponse: (reward) => set({ rewardPerResponse: reward }),
  setActiveBudgetTab: (tab) => set({ activeBudgetTab: tab }),
  setBudgetTabConfirmations: (confirmations) =>
    set({ budgetTabConfirmations: { ...confirmations } }),
  patchBudgetTabConfirmations: (patch) =>
    set((state) => ({
      budgetTabConfirmations: { ...state.budgetTabConfirmations, ...patch },
    })),
  clearBudgetConfiguration: () =>
    set({
      budgetConfigured: false,
      ...initialBudgetState,
      budgetTabConfirmations: { ...EMPTY_TAB_CONFIRMATIONS },
    }),
  clearAllFilters: () =>
    set((state) => ({
      ...withRecomputedModifiedPanels({
        ...state,
        filters: emptyFilters,
      }),
      aiSuggestionsApplied: false,
      audienceMatchCount: undefined,
      profileAttributeFilters: [],
    })),
  restoreAiSuggestedFilters: () =>
    set((state) => ({
      filters: structuredClone(coerceAudienceFilters(state.aiSuggestedFilters)),
      userModifiedPanelIds: [],
      suggestedPanelIds: [...state.suggestedPanelIds],
      filtersModified: true,
      aiSuggestionsApplied: true,
      audienceMatchCount: undefined,
    })),
  markStepComplete: (step) =>
    set((state) => {
      const completedSteps = new Set(state.completedSteps)
      completedSteps.add(step)
      return { completedSteps }
    }),
  setMaxReachedStep: (step) =>
    set((state) => ({
      maxReachedStep: Math.max(state.maxReachedStep, step) as PublishWizardStepNumber,
    })),
  reset: () =>
    set({
      ...initialState,
      completedSteps: new Set(),
      selectedChannels: [...DEFAULT_SELECTED_CHANNELS],
      channelContent: {},
      channelContentSynced: false,
      rewardEnabled: DEFAULT_REWARD_ENABLED,
      budgetConfigured: false,
      budgetTabConfirmations: { ...EMPTY_TAB_CONFIRMATIONS },
      targetingMode: null,
      confidenceLevel: DEFAULT_CONFIDENCE_LEVEL,
      marginOfErrorPercent: DEFAULT_MARGIN_OF_ERROR_PERCENT,
      scientificTargetLocked: false,
      step4Phase: 'mode_selection',
      audienceAvailability: AUDIENCE_AVAILABILITY_EXISTING_ONLY,
    }),
}))

export function getPublishDraftSnapshot(overrides?: {
  targetingMode?: ResponseTargetingMode | null
  step4Phase?: Step4Phase
  scientificTargetLocked?: boolean
}): {
  targetAudience: ReturnType<typeof buildPublishTargetAudienceSnapshot>
  selectedChannels: DistributionChannelId[]
  /** Omitted until channel copy has been loaded or edited, so Save Draft cannot wipe it. */
  channelContent?: ChannelContentPatch
  rewardEnabled: boolean
  budgetConfigured: boolean
  responseTarget: number | null
  rewardPerResponse: number | null
  activeBudgetTab: BudgetWizardTab
  budgetTabConfirmations: BudgetTabConfirmations
  maxReachedStep: PublishWizardStepNumber
  targetingMode?: ResponseTargetingMode | null
  confidenceLevel?: ScientificConfidenceLevel
  marginOfErrorPercent?: number
  scientificTargetLocked?: boolean
  step4Phase?: Step4Phase
  audienceAvailability?: AudienceAvailability
} {
  const state = usePublishStore.getState()
  const targetingMode = overrides?.targetingMode ?? state.targetingMode
  const step4Phase = overrides?.step4Phase ?? state.step4Phase
  const scientificTargetLocked =
    overrides?.scientificTargetLocked ?? state.scientificTargetLocked
  const selectedChannels =
    state.selectedChannels.length > 0
      ? [...state.selectedChannels]
      : [...DEFAULT_SELECTED_CHANNELS]

  return {
    targetAudience: buildPublishTargetAudienceSnapshot({
      filters: state.filters,
      aiSuggestedFilters: state.aiSuggestedFilters,
      suggestedPanelIds: state.suggestedPanelIds,
      userModifiedPanelIds: state.userModifiedPanelIds,
      filtersModified: state.filtersModified,
      aiSuggestionsApplied: state.aiSuggestionsApplied,
      count: state.audienceMatchCount,
      profileAttributeFilters: state.profileAttributeFilters,
    }),
    selectedChannels,
    ...(state.channelContentSynced
      ? { channelContent: channelContentPatchFromDraft(state.channelContent) }
      : {}),
    rewardEnabled: state.rewardEnabled,
    budgetConfigured: state.budgetConfigured,
    responseTarget: state.responseTarget,
    rewardPerResponse: state.rewardPerResponse,
    activeBudgetTab: state.activeBudgetTab,
    budgetTabConfirmations: { ...state.budgetTabConfirmations },
    maxReachedStep: state.maxReachedStep,
    step4Phase,
    scientificTargetLocked,
    ...(targetingMode == null ? {} : { targetingMode }),
    audienceAvailability: state.audienceAvailability,
    ...(targetingMode === 'scientific'
      ? {
          confidenceLevel: state.confidenceLevel,
          marginOfErrorPercent: state.marginOfErrorPercent,
        }
      : {}),
  }
}
